import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'font-poppins inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30 [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0 cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-primary text-white shadow-sm hover:bg-primary/90',
        destructive:
          'border-4 border-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline:
          'border border-input bg-primary-dark-o-1 shadow-xs hover:bg-primary/90 hover:text-white',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost:
          'hover:bg-sidebar hover:text-accent-foreground text-primary-dark font-bold rounded-card focus-visible:ring-0 focus-visible:bg-sidebar focus-visible:text-accent-foreground',
        link: 'text-primary font-bold underline-offset-4 hover:underline',
        bordered: 'font-bold text-sm border-primary border',
        toggle: 'text-primary border-primary border',
        enrich:
          'flex gap-2 transition-all duration-300 ease-in-out [&_svg]:size-auto rounded-lg border-4 border-primary-persian-green bg-white shadow-[0px_1px_7px_1px_rgba(76,69,53,0.21)] font-poppins text-sm font-bold leading-none text-foreground',
        enrich_primary:
          'py-2! bg-white w-full text-foreground text-base font-bold leading-none disabled:opacity-100',
        enrich_secondary: 'bg-primary-dark-o-1 border-none text-sm font-normal',
        enrich_dialog:
          'bg-transparent w-full text-foreground text-base font-bold leading-none border border-primary-aqua-green',
        enrich_menu_outline:
          'bg-transparent text-white text-[15px] font-normal leading-none border border-white [&_svg]:size-4 gap-1.5 px-8! h-auto!',
        enrich_menu:
          'bg-transparent text-white text-[15px] font-normal leading-none [&_svg]:size-4 gap-1.5 border-none justify-start! pl-0! py-1.5! h-auto! rounded-none',
        admin: 'text-xs bg-primary-golden-rod hover:bg-primary-golden-rod/80 p-1',
        admin_secondary: 'text-xs bg-primary-sea-green-mid hover:bg-primary-sea-green-mid/80 p-1',
        wallet:
          'bg-primary-ocean-teal text-white rounded-full font-bold py-3! h-auto! transition-opacity hover:opacity-90 disabled:opacity-40',
        pill: 'text-foreground w-fit gap-1.5 rounded-full border-[1.5px] border-current font-medium px-4! py-1! h-auto!',
      },
      size: {
        xs: 'p-3 h-fit rounded-sm',
        default: 'h-14 px-8 py-3',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-lg px-8',
        icon: 'h-9 w-9 [&_svg]:size-6 p-2',
        'icon-sm': '[&_svg]:size-5 px-2 py-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    if (variant === 'enrich_primary') {
      return (
        <div
          className={cn(
            'from-primary-persian-green to-primary-mint inline-block rounded-[40px] bg-linear-to-r p-1',
            'shadow-[0px_1px_7px_1px_rgba(76,69,53,0.21)]',
            { 'opacity-50': props.disabled },
            className,
          )}
        >
          <Comp className={cn(buttonVariants({ variant, size }))} ref={ref} {...props} />
        </div>
      );
    }
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
